import {
  deleteData,
  getData,
  postData,
  postDataImg,
  updateData,
  updateDataImg,
} from "@/request/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";

export const useRegister = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postDataImg(`developers/apply`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Registration successful!");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message ||
          "Registration failed. Please try again.",
      );
    },
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData(`developers/login`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Login successful!");
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Login failed. Please try again.",
      );
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await postData(`developers/logout`, data);
      return response.data;
    },
    onSuccess: () => {
      // invalidate all queries to ensure fresh data on next login
      queryClient.clear();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Logout failed. Please try again.",
      );
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData(`auth/developer/reset-password`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Password reset link sent!");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message ||
          "Failed to send reset link. Please try again.",
      );
    },
  });
};

export const useGetUser = () => {
  return useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const response = await getData(`developers/profile`);
      return response.data;
    },
    refetchOnWindowFocus: false, // Disable refetch on window focus
    staleTime: 1000 * 60 * 60 * 24, // 24 Hours
    gcTime: 1000 * 60 * 60 * 24, // 24 Hours
    retry: 1,
  });
};

export const useAddProperties = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postDataImg("developers/property/add", data);
      return response.data;
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(res?.message || "Property added successfully.");
    },
    onError: (error: any) => {
      console.log(error);
      toast.error(error?.response?.data?.message || "Failed to add property.");
    },
  });
};

export const useUpdateProperties = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data, id }: any) => {
      const response = await updateData(
        `developers/property/update/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(res?.message || "Property added successfully.");
    },
    onError: (error: any) => {
      console.log(error);
      toast.error(error?.response?.data?.message || "Failed to add property.");
    },
  });
};

export const usePayForProperty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData(`developers/property/pay`, data);
      return response.data;
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(res?.message || "Payment successful.");
    },
    onError: (error: any) => {
      console.log(error);
      toast.error(error?.response?.data?.message || "Payment failed.");
    },
  });
};

export const useUpdatePropertyMedia = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data, id }: any) => {
      const response = await updateDataImg(
        `developers/property/update-media/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(res?.message || "Property media updated successfully.");
    },
    onError: (error: any) => {
      console.log(error);
      toast.error(
        error?.response?.data?.message || "Failed to update property media.",
      );
    },
  });
};

export const useDeleteProperty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId: any) => {
      const response = await deleteData(
        `developers/property/delete/${propertyId}`,
      );
      return response.data;
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(res?.message || "Property deleted successfully.");
    },
    onError: (error: any) => {
      console.log(error);
      toast.error(
        error?.response?.data?.message || "Failed to delete property.",
      );
    },
  });
};

export const useGetProperty = (slug: any) => {
  return useQuery({
    queryKey: ["property", slug],
    queryFn: async () => {
      const response = await getData(`properties/${slug}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchInterval: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};

export const useGetProperties = ({ page = 1, limit = 12, type = "all" }) => {
  return useQuery({
    queryKey: ["properties", page, limit, type],
    queryFn: async () => {
      const response = await getData(`developers/properties`, {
        page,
        limit,
        type,
      });
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};
export const useGetDashboard = () => {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const response = await getData(`developer/dashboard`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};

// PROJECTS HOOKS
export const useRegisterProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postDataImg(`developer/projects`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Project registered successfully!");
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to register project. Please try again.",
      );
    },
  });
};

export const useGetProjects = ({ page = 1, limit = 12 }) => {
  return useQuery({
    queryKey: ["projects", page, limit],
    queryFn: async () => {
      const response = await getData(`developer/projects`, { page, limit });
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};

export const usePayForProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData(`developer/pay-project`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Payment successful!");
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Payment failed. Please try again.",
      );
    },
  });
};

export const useGetProject = (slug: any) => {
  return useQuery({
    queryKey: ["project", slug],
    queryFn: async () => {
      const response = await getData(`developer/projects/${slug}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};

// UNITS
export const useAddNewUnit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postDataImg(`units/add`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Unit added successfully!");
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project"] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to add unit. Please try again.",
      );
    },
  });
};

export const useGetUnits = ({
  page = 1,
  limit = 12,
  project,
}: {
  page?: number;
  limit?: number;
  project?: string;
}) => {
  return useQuery({
    queryKey: ["units", page, limit, project],
    queryFn: async () => {
      const response = await getData(`units`, { page, limit, project });
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};

export const usePayForUnit = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData(`units/pay`, data);
      return response.data;
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Payment failed. Please try again.",
      );
    },
  });
};

export const useGetUnit = (slug: any) => {
  return useQuery({
    queryKey: ["unit", slug],
    queryFn: async () => {
      const response = await getData(`units/${slug}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};

export const useUpdateUnit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ slug, data }: any) => {
      const response = await updateDataImg(`units/${slug}`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Unit updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project"] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
      queryClient.invalidateQueries({ queryKey: ["unit"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to update unit. Please try again.",
      );
    },
  });
};

export const useDeleteUnit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slug: any) => {
      const response = await deleteData(`units/${slug}`);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Unit deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to delete unit. Please try again.",
      );
    },
  });
};

// ADS REQUESTS
export const useCreateAd = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postDataImg(`ads/create`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Ad created successfully!");
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["ads"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to create ad. Please try again.",
      );
    },
  });
};

export const useGetAds = ({ page = 1, limit = 12, status = "all" }) => {
  return useQuery({
    queryKey: ["ads", page, limit, status],
    queryFn: async () => {
      const response = await getData(`ads/requests`, { page, limit, status });
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};

export const usePayAd = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData(`ads/pay-requests`, data);
      return response.data;
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to process payment. Please try again.",
      );
    },
  });
};

/* ── Platform access fee (admin-set) ─────────────────────── */
export const usePayAccessFee = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await postData(`developers/access-fee/pay`, {});
      return response.data;
    },
    onSuccess: (res: any) => {
      if (res?.paymentUrl) window.open(res.paymentUrl, "_blank");
      else toast.success(res?.message || "Done");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to start payment.");
    },
  });
};
